import { useEffect } from 'react';
import { Select } from '@/shared/components/ui/Select';
import { UNIT_GROUPS, VOLUME_UNITS, WEIGHT_UNITS, COUNT_UNITS, convertRateForUnit } from '@/shared/utils/unitConversion';
import { containerPackagingType, abbreviatePackaging } from '@/shared/utils/packagingAbbreviation';

/**
 * The Unit cell for a line item. Picking a different unit RECALCULATES Rate
 * proportionally (₹200/LTR → ₹0.2/ML) rather than leaving it untouched, so
 * typing a quantity in whichever unit the customer actually asked for (500
 * ML, 20 KG …) prices correctly without hand conversion — Qty × Rate, the
 * same formula every totals engine in the app already uses, lands on the
 * right total automatically because Rate now means "per this unit".
 *
 * Volume (ML/LTR/KL) and Weight (G/KG/Quintal/Ton) convert within their own
 * family and stay manually pickable — a rep may deliberately want to quote
 * by the ML or the KG. The packaging CONTAINER (Box/Bucket/Can/Barrel) is
 * never one of those manual choices: it is always derived from the line's
 * own pack size, per RBM's fixed bands (containerPackagingType), so a rep
 * can no longer tag a 210 LTR barrel as "Box" by mistake. Whenever packSize
 * changes and the line isn't currently quoted by a measure unit, this cell
 * silently corrects itself to the right container.
 */
export function UnitPackCell({
  unit, unitPrice, packSize, onChange,
}: {
  unit: string;
  unitPrice: number;
  packSize?: string;
  onChange: (patch: { unit: string; unitPrice: number }) => void;
}) {
  const current = String(unit || '').trim().toUpperCase();
  // A unit the rep picked on purpose — a measure (LTR, KG …) or a count (NOS) —
  // is never replaced by the container derived from the pack size.
  const isMeasureUnit = VOLUME_UNITS.some((u) => u.value === current) || WEIGHT_UNITS.some((u) => u.value === current)
    || COUNT_UNITS.some((u) => u.value === current);

  useEffect(() => {
    if (isMeasureUnit) return;
    const derived = containerPackagingType(packSize);
    if (!derived) return;
    const derivedCode = abbreviatePackaging(derived);
    if (derivedCode.toUpperCase() !== current) {
      onChange({ unit: derivedCode, unitPrice });
    }
    // Only re-derive when the pack size itself changes (or the line switches
    // away from a measure unit) — not on every unitPrice/onChange identity
    // change, which would fight the rep's own typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packSize, isMeasureUnit]);

  const known = UNIT_GROUPS.some((g) => g.units.some((u) => u.value === current));

  const options = [
    // A unit already on the line that isn't one of the standard ones (a
    // custom SKU's own label, or older data) stays selectable and visible
    // instead of silently disappearing the moment this control renders.
    ...(current && !known ? [{ value: current, label: current }] : []),
    ...UNIT_GROUPS.flatMap((g) => g.units.map((u) => ({ value: u.value, label: u.label }))),
  ];

  return (
    <Select
      aria-label="Unit"
      value={current}
      onValueChange={(newUnit) => {
        onChange({ unit: newUnit, unitPrice: convertRateForUnit(current, newUnit, unitPrice) });
      }}
      options={options}
      className="h-9 px-2 pr-7 text-body-sm uppercase"
    />
  );
}

export default UnitPackCell;
